import axios, { type InternalAxiosRequestConfig } from 'axios';
import { getAuthToken } from '@/lib/authStorage';
import { forceLogout, isPublicAuthRoute, refreshAuthToken } from '@/lib/authSession';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000;

type RetriableConfig = InternalAxiosRequestConfig & { _authRetry?: boolean };

export const api = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._authRetry ||
      isPublicAuthRoute(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    originalRequest._authRetry = true;

    const refreshed = await refreshAuthToken();
    if (refreshed) {
      originalRequest.headers.Authorization = `Bearer ${refreshed}`;
      return api(originalRequest);
    }

    await forceLogout();
    return Promise.reject(error);
  },
);

/** Réponse de connexion classique (utilisateur non-admin). */
export interface LoginSuccess {
  access_token: string;
  user: unknown;
}

/** Réponse pour un compte Admin : mot de passe validé, code OTP envoyé par email. */
export interface LoginOtpChallenge {
  requiresOtp: true;
  otpToken: string;
  message: string;
}

export type LoginResponse = LoginSuccess | LoginOtpChallenge;

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { email, password }),
  /** 2ème facteur des comptes Admin : code à 6 chiffres reçu par email. */
  verifyAdminOtp: (otpToken: string, code: string) =>
    api.post<LoginSuccess>('/auth/admin/verify-otp', { otpToken, code }),
  register: (name: string, email: string, password: string, confirmPassword: string) =>
    api.post<{ message: string }>('/auth/register', { name, email, password, confirmPassword }),
  verifyEmail: (email: string, code: string) =>
    api.post<{ access_token: string; user: unknown }>('/auth/verify-email', { email, code }),
  resendVerification: (email: string) =>
    api.post<{ message: string }>('/auth/resend-verification', { email }),
  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string, confirmPassword: string) =>
    api.post<{ access_token: string; user: unknown }>('/auth/reset-password', {
      token,
      password,
      confirmPassword,
    }),
  getProfile: () => api.get('/auth/profile'),
  refresh: () => api.post<{ access_token: string; user: unknown }>('/auth/refresh'),
  deviceGuest: (deviceId: string) =>
    api.post<{ access_token: string; user: unknown }>('/auth/device-guest', { deviceId }),
  convertGuest: (name: string, email: string, password: string, confirmPassword: string) =>
    api.post<{ access_token: string; user: unknown }>('/auth/guest/convert', {
      name,
      email,
      password,
      confirmPassword,
    }),
  deleteAccount: (password?: string) =>
    api.delete<{ message: string }>('/auth/account', { data: { password } }),
};

// Users API (current user / self updates)
export const usersApi = {
  // Self-service : le serveur applique la modif au compte du porteur du jeton
  // (route /users/me). L'ancienne route /users/:id est désormais réservée aux
  // admins pour empêcher toute prise de contrôle d'un autre compte (IDOR).
  update: (data: { name?: string; password?: string; avatarUrl?: string }) =>
    api.patch('/users/me', data),
};

// Public config (anonymous-accessible)
export const publicConfigApi = {
  get: () => api.get<{ guestExamLimit: number; fullExamDurationMinutes: number; fullExamQuestionsCount: number }>('/public/config'),
  getHomepageSections: () => api.get<HomepageSectionData[]>('/public/config/homepage-sections'),
};

// Homepage Sections API
export interface HomepageSectionData {
  id: string;
  title: string;
  titleEn: string | null;
  slug: string;
  description: string | null;
  descriptionEn: string | null;
  icon: string | null;
  color: string | null;
  isVisible: boolean;
  order: number;
  featuredExamIds: string[] | null;
}

export interface HomepageSectionPayload {
  title: string;
  titleEn?: string;
  slug: string;
  description?: string;
  descriptionEn?: string;
  icon?: string;
  color?: string;
  isVisible?: boolean;
  order?: number;
  featuredExamIds?: string[];
}

export const homepageSectionsApi = {
  getAll: () => api.get<HomepageSectionData[]>('/admin/homepage-sections'),
  create: (data: HomepageSectionPayload) => api.post<HomepageSectionData>('/admin/homepage-sections', data),
  update: (id: string, data: Partial<HomepageSectionPayload>) => api.patch<HomepageSectionData>(`/admin/homepage-sections/${id}`, data),
  delete: (id: string) => api.delete(`/admin/homepage-sections/${id}`),
};

export const homepageSectionsImportApi = {
  downloadTemplate: async (locale: 'fr' | 'en' = 'fr') => {
    const response = await api.get('/admin/homepage-sections/import/template', {
      params: { locale },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `sections-template-${locale}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  preview: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/homepage-sections/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commit: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/homepage-sections/import/commit', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Admin API
export const adminApi = {
  getDashboard: () => api.get('/admin/dashboard'),
  getLimits: () => api.get('/admin/limits'),
  updateLimits: (data: Partial<{
    freeDailyPracticeLimit: number;
    freeDailySimulationLimit: number;
    guestExamLimit: number;
    fullExamDurationMinutes: number;
    fullExamQuestionsCount: number;
  }>) => api.patch('/admin/limits', data),
  getUserGrowth: (days?: number) => api.get('/admin/users/growth', { params: { days } }),
  getSessionActivity: (days?: number) => api.get('/admin/sessions/activity', { params: { days } }),
  getRecentUsers: (limit?: number) => api.get('/admin/users/recent', { params: { limit } }),
  getQuestionsByCategory: () => api.get('/admin/questions/by-category'),
  getUsers: (params?: { page?: number; limit?: number; search?: string; role?: string }) =>
    api.get('/admin/users', { params }),
  getUserById: (id: string) => api.get(`/admin/users/${id}`),
  updateUserRole: (id: string, role: string) => api.patch(`/admin/users/${id}/role`, { role }),
  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
  // Analytics : meilleurs users, examens populaires, détail par utilisateur.
  getTopUsers: (limit?: number) => api.get('/admin/users/top', { params: { limit } }),
  getPopularExams: (limit?: number) => api.get('/admin/exams/popular', { params: { limit } }),
  getUserStats: (id: string, days?: number) =>
    api.get(`/admin/users/${id}/stats`, { params: { days } }),
  getUserSessions: (id: string, limit?: number) =>
    api.get(`/admin/users/${id}/sessions`, { params: { limit } }),
  // Activité première partie (self-hosted, sans GA).
  getActivitySummary: (days?: number) =>
    api.get<ActivitySummary>('/admin/activity/summary', { params: { days } }),
  getActivityRecent: (page?: number, limit?: number) =>
    api.get<ActivityRecentPage>('/admin/activity/recent', { params: { page, limit } }),
};

export interface ActivityRecentPage {
  items: ActivityEventRow[];
  total: number;
  page: number;
  limit: number;
}

export interface ActivitySummary {
  totalEvents: number;
  activeUsers: number;
  byEvent: { name: string; count: number }[];
  topExams: { examId: string; examName: string; views: number }[];
  topSearches: { term: string; count: number }[];
  topCategories: { categoryId: string; categoryName: string; clicks: number }[];
  abandon: { count: number; avgAnswered: number };
}

export interface ActivityEventRow {
  id: string;
  userId: string | null;
  userEmail: string | null;
  isGuest: boolean;
  name: string;
  examId: string | null;
  examName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  searchTerm: string | null;
  numValue: number | null;
  deviceCategory: string | null;
  path: string | null;
  metadata: string | null;
  createdAt: string;
}

// Exams API
export const examsApi = {
  getAll: (params?: { includeInactive?: boolean; page?: number; limit?: number; search?: string; sort?: string; dir?: string }) => api.get('/exams', { params }),
  getById: (id: string) => api.get(`/exams/${id}`),
  create: (data: any) => api.post('/exams', data),
  update: (id: string, data: any) => api.patch(`/exams/${id}`, data),
  delete: (id: string) => api.delete(`/exams/${id}`),
};

// Categories API
export const categoriesApi = {
  getAll: (params?: { examId?: string; includeInactive?: boolean; page?: number; limit?: number; search?: string; sort?: string; dir?: string }) =>
    api.get('/categories', { params }),
  getByExam: (examId: string, params?: { includeInactive?: boolean }) =>
    api.get(`/categories/exam/${examId}`, { params }),
  getById: (id: string) => api.get(`/categories/${id}`),
  create: (data: any) => api.post('/categories', data),
  update: (id: string, data: any) => api.patch(`/categories/${id}`, data),
  delete: (id: string) => api.delete(`/categories/${id}`),
};

export const categoriesImportApi = {
  downloadTemplate: async (locale: 'fr' | 'en' = 'fr') => {
    const response = await api.get('/categories/import/template', {
      params: { locale },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `categories-template-${locale}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  preview: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/categories/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commit: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/categories/import/commit', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Questions API
export const questionsApi = {
  getAll: (params?: { categoryId?: string; includeInactive?: boolean; limit?: number; offset?: number; search?: string }) =>
    api.get('/questions', { params }),
  getById: (id: string) => api.get(`/questions/${id}`),
  getRandomByExam: (examId: string, count: number, locale?: string) =>
    api.get(`/questions/exam/${examId}/random`, { params: { count, locale } }),
  create: (data: any) => api.post('/questions', data),
  update: (id: string, data: any) => api.put(`/questions/${id}`, data),
  delete: (id: string) => api.delete(`/questions/${id}`),
  toggleActive: (id: string) => api.patch(`/questions/${id}/toggle-active`),
};

// Questions Import API (Excel upload)
export const questionsImportApi = {
  downloadTemplate: async () => {
    const response = await api.get('/questions/import/template', {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'questions-template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  preview: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/questions/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // parsing thousands of rows can exceed the default 30s
    });
  },
  commit: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/questions/import/commit', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // bulk-insert of thousands of questions: no client-side timeout
    });
  },
};

export const examsImportApi = {
  downloadTemplate: async (locale: 'fr' | 'en' = 'fr') => {
    const response = await api.get('/exams/import/template', {
      params: { locale },
      responseType: 'blob',
    });
    const suffix = locale === 'en' ? 'en' : 'fr';
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `exams-template-${suffix}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  preview: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/exams/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commit: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/exams/import/commit', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Sessions API
export const sessionsApi = {
  start: (data: any) => api.post('/sessions/start', data),
  getById: (id: string) => api.get(`/sessions/${id}`),
  submitAnswer: (sessionId: string, data: any) => api.post(`/sessions/${sessionId}/answer`, data),
  complete: (sessionId: string, data?: any) => api.patch(`/sessions/${sessionId}/complete`, data),
  recordPractice: (data: {
    examId: string;
    categoryId: string;
    score: number;
    correctCount: number;
    totalQuestions: number;
    timeSpent: number;
    xpEarned?: number;
  }) => api.post('/sessions/practice/record', data),
  recordSimulation: (data: {
    examId: string;
    score: number;
    correctCount: number;
    totalQuestions: number;
    timeSpent: number;
    xpEarned?: number;
    abandoned?: boolean;
  }) => api.post('/sessions/simulation/record', data),
  getHistory: (params?: any) => api.get('/sessions/history', { params }),
  getStats: () => api.get('/sessions/stats'),
  getStatsByCategory: () => api.get('/sessions/stats/by-category'),
  getRecentActivity: (days?: number) =>
    api.get('/sessions/stats/recent-activity', { params: { days } }),
};

// Stats API
export const statsApi = {
  getOverview: (days?: number) => api.get('/stats/me', { params: { days } }),
};

// Gamification API
export const gamificationApi = {
  getProgress: (userId: string) => api.get(`/gamification/user/${userId}/progress`),
  getLeaderboard: (limit?: number) => api.get('/gamification/leaderboard', { params: { limit } }),
  getUserRank: (userId: string) => api.get(`/gamification/user/${userId}/rank`),
  getUserRanking: (userId: string) => api.get(`/gamification/user/${userId}/ranking`),
  getAchievements: () => api.get('/gamification/achievements'),
  getUnseenAchievements: () => api.get('/gamification/achievements/unseen'),
  markAchievementSeen: (id: string) => api.patch(`/gamification/achievements/${id}/seen`),
};
